import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  TextField,
  MenuItem,
  Chip,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  EventAvailable,
  ChevronRight,
  WarningAmber,
  PersonOff,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import hrTimePayrollService from '../../services/hrTimePayrollService';
import hrService, { Department } from '../../services/hrService';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { ErrorState, TableEmptyState, TableSurface, headerCellSx, bodyRowSx } from '../../components/common/pageKit';

// ---- pure formatters ----
const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const fmtInt = (v: any) => (isNum(v) ? Number(v).toLocaleString('en-IN') : '—');
const fmtNum1 = (v: any) => (isNum(v) ? Number(v).toFixed(1) : '—');

const todayStr = () => new Date().toISOString().slice(0, 10);

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const EXCEPTION_LABEL: Record<string, string> = {
  MISSING_PUNCH: 'Missing Punch',
  ABSENT_NO_LEAVE: 'Absent, No Leave',
  LATE: 'Late Check-in',
};

const STATUS_LETTER: Record<string, string> = {
  PRESENT: 'P',
  ABSENT: 'A',
  ON_LEAVE: 'L',
  HALF_DAY: 'H',
  WFH: 'W',
  HOLIDAY: 'O',
  WEEK_OFF: 'O',
};

const STATUS_LABEL: Record<string, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  ON_LEAVE: 'On Leave',
  HALF_DAY: 'Half-Day',
  WFH: 'Work From Home',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'Week Off',
};

const AttendanceControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const T = useChartTokens();

  // ---- Board (date-scoped) ----
  const [date, setDate] = useState<string>(todayStr());
  const [board, setBoard] = useState<any>(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState<string | null>(null);

  const loadBoard = useCallback((forDate: string) => {
    setBoardLoading(true);
    setBoardError(null);
    hrTimePayrollService
      .attendanceBoard(forDate)
      .then((res: any) => setBoard(res))
      .catch((e: any) => setBoardError(e?.message || 'Failed to load the attendance board.'))
      .finally(() => setBoardLoading(false));
  }, []);

  useEffect(() => {
    loadBoard(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // ---- Trend (independent) ----
  const [trend, setTrend] = useState<any[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState<string | null>(null);

  const loadTrend = useCallback(() => {
    setTrendLoading(true);
    setTrendError(null);
    hrTimePayrollService
      .attendanceTrend(30)
      .then((res: any) => setTrend(Array.isArray(res) ? res : []))
      .catch((e: any) => setTrendError(e?.message || 'Failed to load the attendance trend.'))
      .finally(() => setTrendLoading(false));
  }, []);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  // ---- Departments (independent, for matrix filter) ----
  const [departments, setDepartments] = useState<Department[]>([]);
  useEffect(() => {
    hrService
      .getDepartments()
      .then((res) => setDepartments(res?.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  // ---- Matrix (month/year/department-scoped) ----
  const now = new Date();
  const [matrixMonth, setMatrixMonth] = useState<number>(now.getMonth() + 1);
  const [matrixYear, setMatrixYear] = useState<number>(now.getFullYear());
  const [matrixDept, setMatrixDept] = useState<number | ''>('');
  const [matrix, setMatrix] = useState<any>(null);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [matrixError, setMatrixError] = useState<string | null>(null);

  const loadMatrix = useCallback(() => {
    setMatrixLoading(true);
    setMatrixError(null);
    hrTimePayrollService
      .attendanceMatrix({ month: matrixMonth, year: matrixYear, departmentId: matrixDept ? Number(matrixDept) : undefined })
      .then((res: any) => setMatrix(res))
      .catch((e: any) => setMatrixError(e?.message || 'Failed to load the attendance matrix.'))
      .finally(() => setMatrixLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixMonth, matrixYear, matrixDept]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const summary = board?.summary || {};
  const byDepartment: any[] = board?.byDepartment || [];
  const exceptions: any[] = board?.exceptions || [];
  const recentCheckins: any[] = board?.recentCheckins || [];

  const kpiDefs = useMemo(
    () => [
      { key: 'present', label: 'Present', value: fmtInt(summary.present), color: C.good },
      { key: 'absent', label: 'Absent', value: fmtInt(summary.absent), color: C.bad },
      { key: 'onLeave', label: 'On Leave', value: fmtInt(summary.onLeave), color: C.cyan },
      { key: 'wfh', label: 'WFH', value: fmtInt(summary.wfh), color: C.purple },
      { key: 'halfDay', label: 'Half-Day', value: fmtInt(summary.halfDay), color: C.warn },
      { key: 'late', label: 'Late', value: summary.late === null || summary.late === undefined ? '—' : fmtInt(summary.late), color: C.warn },
      { key: 'notMarked', label: 'Not Marked', value: fmtInt(summary.notMarked), color: summary.notMarked > 0 ? C.bad : C.text },
      { key: 'overtime', label: 'Overtime Hrs', value: fmtNum1(summary.overtimeHours), color: C.text },
    ],
    [summary, C]
  );

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, minWidth: 0 }}>
      <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{ fontWeight: 800, fontSize: { xs: 17, sm: 19, md: 20, lg: 22 }, lineHeight: 1.15, color: color || C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {value}
      </Typography>
    </Box>
  );

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
      {children}
    </Typography>
  );

  const Panel: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, children, action }) => (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <SectionLabel>{title}</SectionLabel>
        {action}
      </Stack>
      <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, height: '100%' }}>{children}</Box>
    </Box>
  );

  const STATUS_COLOR: Record<string, string> = useMemo(
    () => ({
      PRESENT: C.good,
      WFH: T.isDark ? '#2dd4bf' : '#0d9488',
      HALF_DAY: C.warn,
      ABSENT: C.bad,
      ON_LEAVE: C.cyan,
      HOLIDAY: C.sub,
      WEEK_OFF: C.sub,
    }),
    [C, T.isDark]
  );

  // ---- 30-day trend chart: present vs absent counts + attendance % on secondary axis ----
  const TrendChart: React.FC = () => {
    if (trendError) return <ErrorState message={trendError} onRetry={loadTrend} />;
    if (!trend.length) return <Typography sx={{ color: C.sub, p: 1 }}>No attendance history in this window.</Typography>;
    const rows = trend.map((r: any) => ({
      ...r,
      day: r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—',
    }));
    return (
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps(T)} />
            <XAxis dataKey="day" {...axisProps(T)} minTickGap={24} />
            <YAxis yAxisId="count" {...axisProps(T)} width={44} allowDecimals={false} />
            <YAxis yAxisId="pct" orientation="right" {...axisProps(T)} width={44} tickFormatter={(v: number) => `${v}%`} domain={[0, 100]} />
            <RTooltip
              {...tooltipStyle(T)}
              formatter={(v: any, name: any) => (name === 'Attendance %' ? [isNum(v) ? `${Number(v).toFixed(1)}%` : '—', name] : [fmtInt(v), name])}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
            <Bar yAxisId="count" dataKey="present" name="Present" fill={T.series(0)} radius={[3, 3, 0, 0]} maxBarSize={12} />
            <Bar yAxisId="count" dataKey="absent" name="Absent" fill={T.series(1)} radius={[3, 3, 0, 0]} maxBarSize={12} />
            <Line yAxisId="pct" type="monotone" dataKey="attendancePct" name="Attendance %" stroke={T.series(2)} strokeWidth={2.2} dot={false} connectNulls activeDot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // ---- Matrix ----
  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [now]);

  const MatrixGrid: React.FC = () => {
    if (matrixError) return <ErrorState message={matrixError} onRetry={loadMatrix} />;
    const employees: any[] = matrix?.employees || [];
    const dates: string[] = matrix?.dates || [];
    if (!matrixLoading && !employees.length) {
      return <TableEmptyState title="No employees found" hint="Try a different month or department." />;
    }
    return (
      <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 1 }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', width: 'max-content', fontSize: 12 }}>
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  bgcolor: C.panel2,
                  color: C.sub,
                  textAlign: 'left',
                  p: 1,
                  minWidth: 160,
                  borderBottom: `1px solid ${C.line}`,
                  borderRight: `1px solid ${C.line}`,
                }}
              >
                Employee
              </Box>
              {dates.map((d) => (
                <Box
                  component="th"
                  key={d}
                  sx={{ p: '4px 2px', color: C.sub, bgcolor: C.panel2, borderBottom: `1px solid ${C.line}`, minWidth: 26, fontWeight: 600 }}
                >
                  {Number(d.slice(8, 10))}
                </Box>
              ))}
              <Box component="th" sx={{ p: 1, color: C.sub, bgcolor: C.panel2, borderBottom: `1px solid ${C.line}`, borderLeft: `1px solid ${C.line}`, minWidth: 46 }}>P</Box>
              <Box component="th" sx={{ p: 1, color: C.sub, bgcolor: C.panel2, borderBottom: `1px solid ${C.line}`, minWidth: 46 }}>A</Box>
              <Box component="th" sx={{ p: 1, color: C.sub, bgcolor: C.panel2, borderBottom: `1px solid ${C.line}`, minWidth: 46 }}>L</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {employees.map((emp: any) => (
              <Box component="tr" key={emp.employeeId}>
                <Box
                  component="td"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: C.panel,
                    p: 1,
                    borderBottom: `1px solid ${C.line}`,
                    borderRight: `1px solid ${C.line}`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 200,
                  }}
                  title={`${emp.name} (${emp.employeeCode || '—'})`}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.2 }} noWrap>
                    {emp.name}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: C.sub, lineHeight: 1.2 }} noWrap>
                    {emp.employeeCode || '—'}
                  </Typography>
                </Box>
                {dates.map((d) => {
                  const status: string | undefined = emp.days ? emp.days[d] : undefined;
                  const letter = status ? STATUS_LETTER[status] || '?' : '·';
                  const color = status ? STATUS_COLOR[status] : undefined;
                  return (
                    <Box
                      component="td"
                      key={d}
                      title={`${d}: ${status ? STATUS_LABEL[status] || status : 'No record'}`}
                      sx={{
                        textAlign: 'center',
                        borderBottom: `1px solid ${C.line}`,
                        color: color ? '#fff' : C.sub,
                        bgcolor: color ? `${color}cc` : 'transparent',
                        fontWeight: 700,
                        fontSize: 10.5,
                        py: '3px',
                      }}
                    >
                      {letter}
                    </Box>
                  );
                })}
                <Box component="td" sx={{ textAlign: 'center', borderBottom: `1px solid ${C.line}`, borderLeft: `1px solid ${C.line}`, color: C.good, fontWeight: 700 }}>
                  {fmtInt(emp.summary?.present)}
                </Box>
                <Box component="td" sx={{ textAlign: 'center', borderBottom: `1px solid ${C.line}`, color: C.bad, fontWeight: 700 }}>
                  {fmtInt(emp.summary?.absent)}
                </Box>
                <Box component="td" sx={{ textAlign: 'center', borderBottom: `1px solid ${C.line}`, color: C.cyan, fontWeight: 700 }}>
                  {fmtInt(emp.summary?.onLeave)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const exceptionsByType = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const ex of exceptions) {
      const t = ex.type || 'OTHER';
      if (!groups[t]) groups[t] = [];
      groups[t].push(ex);
    }
    return groups;
  }, [exceptions]);

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${C.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EventAvailable sx={{ color: C.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Attendance Control Center</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Who's in, who's out, and where the gaps are.</Typography>
          </Box>
        </Stack>
        <TextField
          type="date"
          size="small"
          label="Board date"
          value={date}
          onChange={(e) => setDate(e.target.value || todayStr())}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170 }}
        />
      </Box>

      {(boardLoading || trendLoading || matrixLoading) && <LinearProgress />}

      {boardError && !board ? (
        <ErrorState message={boardError} onRetry={() => loadBoard(date)} />
      ) : (
        <>
          {/* KPI band */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(120px, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {kpiDefs.map((k) => (
              <Metric key={k.key} label={k.label} value={k.value} color={k.color} />
            ))}
          </Box>

          {/* By department + exceptions */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 2 }}>
            <Panel title="By Department">
              {byDepartment.length ? (
                <TableSurface>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Department</TableCell>
                        <TableCell sx={headerCellSx} align="right">Headcount</TableCell>
                        <TableCell sx={headerCellSx} align="right">Present</TableCell>
                        <TableCell sx={headerCellSx} align="right">Absent</TableCell>
                        <TableCell sx={headerCellSx} align="right">On Leave</TableCell>
                        <TableCell sx={headerCellSx} align="right">Not Marked</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byDepartment.map((row: any, i: number) => (
                        <TableRow key={i} sx={bodyRowSx}>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.department}>
                            {row.department || '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtInt(row.headcount)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'success.main' }}>{fmtInt(row.present)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'error.main' }}>{fmtInt(row.absent)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtInt(row.onLeave)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap', color: row.notMarked > 0 ? 'warning.main' : 'inherit', fontWeight: row.notMarked > 0 ? 700 : 400 }}
                          >
                            {fmtInt(row.notMarked)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableSurface>
              ) : (
                <TableEmptyState title="No department data" hint="No active employees found for this date." />
              )}
            </Panel>

            <Panel title="Exceptions">
              {exceptions.length ? (
                <Stack spacing={1.5}>
                  {Object.entries(exceptionsByType).map(([type, items]) => (
                    <Box key={type}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Chip
                          size="small"
                          label={`${EXCEPTION_LABEL[type] || type} (${items.length})`}
                          sx={{ fontSize: 10.5, height: 22, bgcolor: `${C.warn}1f`, color: C.warn, fontWeight: 700 }}
                        />
                      </Stack>
                      <Stack spacing={0.5} sx={{ maxHeight: 160, overflowY: 'auto' }}>
                        {items.map((ex: any, i: number) => (
                          <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: C.panel2 } }}>
                            <Typography sx={{ fontSize: 12.5 }} noWrap title={`${ex.employeeName} (${ex.employeeCode || '—'})`}>
                              {ex.employeeName || '—'} <Box component="span" sx={{ color: C.sub }}>({ex.employeeCode || '—'})</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: C.sub, flexShrink: 0, ml: 1 }} noWrap>
                              {ex.detail}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <TableEmptyState title="No exceptions today" hint="No missing punches, unexplained absences, or late check-ins." />
              )}
            </Panel>
          </Box>

          {/* Trend */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Panel title="30-Day Attendance Trend">
              <TrendChart />
            </Panel>
          </Box>

          {/* Monthly matrix */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Panel
              title="Monthly Attendance Matrix"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={matrixMonth}
                    onChange={(e) => setMatrixMonth(Number(e.target.value))}
                    sx={{ minWidth: 100 }}
                  >
                    {MONTH_NAMES.map((m, i) => (
                      <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Year"
                    value={matrixYear}
                    onChange={(e) => setMatrixYear(Number(e.target.value))}
                    sx={{ minWidth: 90 }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Department"
                    value={matrixDept}
                    onChange={(e) => setMatrixDept(e.target.value === '' ? '' : Number(e.target.value))}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="">All Departments</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.dept_name}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              }
            >
              <MatrixGrid />
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1.5 }}>
                {Object.entries(STATUS_LABEL).map(([key, label]) => (
                  <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: STATUS_COLOR[key] || C.sub }} />
                    <Typography sx={{ fontSize: 11, color: C.sub }}>{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Panel>
          </Box>

          {/* Recent check-ins */}
          {recentCheckins.length > 0 && (
            <Box sx={{ px: 3, pb: 3 }}>
              <Panel
                title="Recent Check-ins"
                action={
                  <Button
                    size="small"
                    endIcon={<ChevronRight />}
                    onClick={() => navigate('/hr/attendance')}
                    sx={{ color: C.accent }}
                  >
                    All Attendance
                  </Button>
                }
              >
                <Stack spacing={0.5}>
                  {recentCheckins.map((r: any, i: number) => (
                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.5, py: 0.5 }}>
                      <Typography sx={{ fontSize: 12.5 }} noWrap>{r.employeeName || '—'}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: C.sub, flexShrink: 0, ml: 1 }}>
                        {r.check_in ? new Date(r.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Panel>
            </Box>
          )}

          {!boardLoading && !board && (
            <Box sx={{ px: 3, pb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PersonOff sx={{ fontSize: 16, color: C.sub }} />
                <Typography sx={{ color: C.sub, fontStyle: 'italic' }}>No attendance board data for this date.</Typography>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AttendanceControlCenter;
